import { Modal, Spin } from "antd";
import { useState, useEffect, useRef, useLayoutEffect } from "react";
import {
  verifyTokenLoad,
  verifyPromoCodeLoad,
  resetStateForPromoCode,
} from "../../redux/slices/subscription.slice";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { navigate } from "../../common/navigator";
import { debounce } from "../../utils/global-func";
import {
  Check,
  Sparkles,
  Crown,
  Zap,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

function AccessDeniedCard() {
  return (
    <div className="min-h-screen pricing-bg relative overflow-hidden flex items-center justify-center px-4 py-12">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/10"></div>

      {/* Glow blobs */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
      </div>

      {/* Card */}
      <div className="relative z-10 bg-white/10 backdrop-blur-xl rounded-3xl p-10 max-w-lg w-full border-2 border-white/30 shadow-2xl text-center">
        {/* Icon */}
        <div className="flex justify-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-white/30 to-white/10 backdrop-blur-sm flex items-center justify-center border border-white/40">
            <span className="text-white text-3xl font-bold">!</span>
          </div>
        </div>

        {/* Heading */}
        <h1 className="text-4xl font-bold text-white mb-3">
          Sign In to Continue
        </h1>

        {/* Description */}
        <p className="text-white/70 text-lg leading-relaxed mb-10">
          You’re not logged in yet. Please sign in to continue or open the app
          using your active MentalFit account.
        </p>

        {/* CTA */}
        <button
          onClick={() => (window.location.href = "https://mpubx.app.link/open")}
          className="w-full py-4 rounded-xl font-bold text-lg transition-all duration-300
                     bg-white text-[#A66296] hover:bg-white/90 shadow-lg
                     hover:shadow-xl hover:scale-105"
        >
          Go to Login
        </button>
      </div>
    </div>
  );
}

function SimpleLoader() {
  return (
    <div
      className="
        fixed inset-0 
        pricing-bg
        flex items-center justify-center 
        z-50
      "
    >
      <style>
        {`
          @keyframes spinGradient {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      {/* Loader Ring */}
      <div
        className="
          h-20 w-20 rounded-full border-4 
          border-t-transparent
          border-l-transparent
          border-r-[#fde5ea] 
          border-b-[#d9d8ff]
          shadow-xl
        "
        style={{
          animation: "spinGradient 1s linear infinite",
        }}
      ></div>
    </div>
  );
}

function SubscriptionScreen() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token =
    params.get("token") || localStorage.getItem("refLoginCompleteToken");
  const type = params.get("type") !== "null" ? params.get("type") : "user";

  const dispatch = useDispatch();
  const {
    verificationLoading,
    verificationData,
    promoCodeData,
    verifypromoCodeLoading,
  } = useSelector((state) => state?.subscriptionReducer || {});
  const [selectedPlan, setSelectedPlan] = useState();
  const [confirmPaymentModal, setConfirmPaymentModal] = useState(false);
  const [planType] = useState(type);
  const [promoCode, setPromoCode] = useState("");
  const [amount, setAmount] = useState(null);

  // Add this state at the top of your component
  const [currentIndex, setCurrentIndex] = useState(1);
  const scrollContainerRef = useRef(null);

  // Add these functions
  const scrollToIndex = (index) => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const cardWidth = container.offsetWidth;
      container.scrollTo({
        left: cardWidth * index,
        behavior: "smooth",
      });
      setCurrentIndex(index);
    }
  };

  const handlePrev = () => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : plans.length - 1;
    scrollToIndex(newIndex);
  };

  const handleNext = () => {
    const newIndex = currentIndex < plans.length - 1 ? currentIndex + 1 : 0;
    scrollToIndex(newIndex);
  };

  const handleSubmit = (e) => {
    localStorage.clear();
    e.preventDefault();

    const queryParams = new URLSearchParams({
      amount: amount,
      planType,
      subType: plans.find((p) => p.id === selectedPlan)?.id,
      plan: selectedPlan,
      token: token,
      promoCode: promoCodeData?.isVerify ? promoCode : "",
    }).toString();

    navigate(`/paymentSummary?${queryParams}`);
  };

  // Define all plan data
  const subscriptionPlans = [
    {
      id: "Subscription Monthly",
      name: "Monthly",
      price: "3.99",
      billing: "/month",
      trial: "30-day free trial included",
      features: [
        "Full access to premium wellness tools",
        "Cancel anytime",
        "Perfect for staying flexible",
      ],
      cta: "Get Monthly",
      icon: Sparkles,
      color: "#7CF5D3",
    },

    {
      id: "Subscription Yearly",
      name: "Yearly",
      price: "39.99",
      billing: "/year",
      popular: true, // Badge shown
      savings: "Save 17%",
      trial: "30-day free trial included",
      features: [
        "Best value",
        "Save more each year",
        "Stay committed to your wellness journey",
      ],
      cta: "Get Yearly",
      icon: Crown,
      color: "#4FC3FF",
    },

    {
      id: "Subscription Lifetime",
      name: "Lifetime Access",
      price: "129.99",
      trial: "one-time",
      features: ["Unlock access forever", "One-time payment", "No renewals"],
      cta: "Get Lifetime",
      icon: Zap,
      color: "#F38BC8",
    },
  ];

  const proPlans = [
    {
      id: "Pro Monthly",
      name: "Become a Pro",
      price: "29.99",
      billing: "/month",
      trial: "30-day free trial included",
      features: [
        "Get discovered by new clients",
        "Offer sessions directly in the app",
        "Track earnings with Stripe dashboard",
        "Payouts sent directly to your Stripe account",
        "In-app messaging",
        "Secure payments",
        "Grow your wellness business",
      ],
      cta: "Become a Pro",
      icon: Sparkles,
      color: "#7CF5D3",
    },

    {
      id: "Pro Yearly",
      name: "Become a Pro – Yearly",
      price: "299.99",
      billing: "/year",
      equivalentMonthly: "$24.99/month billed yearly",
      savings: "Save $58.99/year",
      trial: "30-day free trial included",
      features: [
        "Everything included in Pro Monthly",
        "Lower overall annual cost",
        "Grow your client base with long-term stability",
      ],
      cta: "Get Pro Yearly",
      icon: Crown,
      color: "#4FC3FF",
      popular: true, // Badge shown
    },
  ];

  useLayoutEffect(() => {
    if (scrollContainerRef.current) {
      scrollToIndex(currentIndex);
    }
  }, [scrollContainerRef.current]);

  // Choose the plans to display based on planType
  const plans = planType === "pro" ? proPlans : subscriptionPlans;

  // Dynamic heading & description
  const heading =
    planType === "pro" ? "Become a MentalFit Pro" : "Your Wellness, Elevated";
  const description =
    planType === "pro"
      ? `Share your expertise, grow your client base, and unlock tools that help you deliver
meaningful wellness support all in one place.`
      : "Pick the MentalFit plan that fits your lifestyle.";

  const footer =
    planType === "pro"
      ? "Grow your impact with MentalFit"
      : "Every plan is designed to support your mental wellness journey.";

  useEffect(() => {
    dispatch(verifyTokenLoad({ token }));

    return () => dispatch(resetStateForPromoCode());
  }, [dispatch]);

  useEffect(() => {
    if (selectedPlan) {
      setAmount(plans.find((p) => p.id === selectedPlan)?.price);
    }
    if (promoCodeData?.isVerify) {
      let oldAmount = plans.find((p) => p.id === selectedPlan)?.price;
      let new_amount = parseFloat(
        oldAmount - (oldAmount * promoCodeData?.discount) / 100
      ).toFixed(2);
      setAmount(new_amount);
    }
  }, [selectedPlan, promoCodeData]);

  if (verificationLoading) return <SimpleLoader />;

  if (!verificationLoading && !verificationData?.isVerify)
    return <AccessDeniedCard />;

  return (
    <div className="min-h-screen pricing-bg py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto flex flex-col gap-5 ">
        <div className="text-center">
          <h1 className="text-5xl sm:text-4xl font-bold text-white mb-2 sm:mb-4">
            {heading}
          </h1>
          <p className="text-xl sm:text-lg lg:text-xl text-white/90 max-w-2xl mx-auto px-2">
            {description}
          </p>
        </div>

        <div
          className="
    relative
    w-full
    sm:max-w-full
    md:max-w-4xl
    lg:max-w-6xl
    xl:max-w-7xl
    mx-auto
    sm:mb-8
  "
        >
          {/* Navigation Buttons */}
          <button
            onClick={handlePrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-40 bg-white/20 backdrop-blur-xl border border-white/30 text-white p-3 rounded-full hover:bg-white/30 transition-all duration-300 shadow-xl hover:scale-180"
            aria-label="Previous plan"
          >
            <ChevronLeft className="w-6 sm:w-4 h-6 sm:h-4" />
          </button>

          <button
            onClick={handleNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-40 bg-white/20 backdrop-blur-xl border border-white/30 text-white p-3 rounded-full hover:bg-white/30 transition-all duration-300 shadow-xl hover:scale-110"
            aria-label="Next plan"
          >
            <ChevronRight className="w-6 sm:w-4 h-6 sm:h-4" />
          </button>

          {/* Carousel Container */}
          <div
            ref={scrollContainerRef}
            className="overflow-x-hidden overflow-y-visible scroll-smooth"
            style={{ scrollSnapType: "x mandatory" }}
          >
            <div className="flex items-stretch">
              {plans.map((plan) => {
                const Icon = plan.icon;

                return (
                  <div
                    key={plan.id}
                    className="
              flex-shrink-0
              w-[90%] sm:w-[60%] md:w-full
              px-4
              pt-8 md:pt-0
            "
                    style={{ scrollSnapAlign: "center" }}
                  >
                    <div className="relative group h-full flex">
                      {/* Popular Badge */}
                      {plan.popular && (
                        <div className="absolute top-0 right-0 z-30">
                          <div className="bg-gradient-to-r from-yellow-400 to-orange-400 text-gray-900 text-md font-bold px-4 py-1 rounded-bl-xl rounded-tr-3xl shadow-md flex items-center gap-1">
                            <Crown className="w-5 h-5" />
                            Popular
                          </div>
                        </div>
                      )}

                      {/* Card */}
                      <div
                        className={`flex flex-col w-full bg-white/10 backdrop-blur-xl rounded-3xl transition-all duration-500 ${
                          plan.popular
                            ? "border-2 border-white/40 shadow-2xl"
                            : "border border-white/20 hover:border-white/40 shadow-xl"
                        }`}
                      >
                        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0  transition-opacity duration-500"></div>

                        <div className="relative p-8 flex flex-col gap-2 h-full">
                          {/* Icon */}
                          <div className="flex items-center justify-between mb-6 sm:mt-3">
                            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-sm flex items-center justify-center border border-white/30">
                              <Icon className="w-7 h-7 text-white" />
                            </div>
                          </div>

                          {/* Title */}
                          <h3
                            className="text-3xl font-bold mb-2"
                            style={{ color: plan?.color }}
                          >
                            {plan.name}
                          </h3>

                          {/* Price */}
                          <div className="flex items-baseline gap-2 mb-4">
                            <span className="font-extrabold text-white text-[clamp(2rem,5vw,6rem)]">
                              ${plan.price}
                            </span>
                            <span className="text-white/80 text-[clamp(0.875rem,3vw,1.25rem)]">
                              {plan.billing}
                            </span>
                          </div>

                          {/* Savings */}
                          {plan.savings && (
                            <div className="mb-3 inline-block">
                              <span className="bg-yellow-400/20 text-yellow-100 px-3 py-1 rounded-full text-sm font-semibold border border-yellow-400/30">
                                {plan.savings}
                              </span>
                            </div>
                          )}

                          {/* Trial */}
                          {plan.trial && (
                            <p className="text-white/90 font-semibold mb-6 text-lg">
                              {plan.trial}
                            </p>
                          )}

                          {/* Features */}
                          <div className="flex-grow mb-8">
                            <ul className="space-y-4">
                              {plan.features.map((feature, index) => (
                                <li
                                  key={index}
                                  className="flex items-start gap-3 text-white/90"
                                >
                                  <div className="flex-shrink-0 w-5 h-5 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mt-0.5">
                                    <Check className="w-3 h-3 text-white" />
                                  </div>
                                  <span className="text-base leading-relaxed">
                                    {feature}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* CTA */}
                          <button
                            onClick={() => {
                              setSelectedPlan(plan.id);
                              setConfirmPaymentModal(true);
                            }}
                            className={`w-full py-4 px-6 rounded-xl font-bold text-lg transition-all duration-300 ${
                              plan.popular
                                ? "bg-white text-[#A66296] hover:bg-white/90 shadow-lg hover:shadow-xl hover:scale-105"
                                : "bg-white/20 backdrop-blur-sm text-white border-2 border-white/40 hover:bg-white hover:text-[#A66296] hover:scale-105"
                            }`}
                          >
                            {plan.cta || "Select Plan"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {plans.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToIndex(index)}
                className={`transition-all duration-300 rounded-full ${
                  currentIndex === index
                    ? "w-8 h-3 bg-white"
                    : "w-3 h-3 bg-white/40 hover:bg-white/60"
                }`}
                aria-label={`Go to plan ${index + 1}`}
              />
            ))}
          </div>
        </div>

        <div className="text-center">
          <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-lg px-6 py-4 rounded-2xl border border-white/20">
            <span className="text-white/80 text-sm font-medium">
              Powered by
            </span>
            <svg
              className="h-8"
              viewBox="0 0 60 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.53-.24 1.53-1C6.26 13.77 0 14.51 0 9.95 0 7.04 2.28 5.3 5.62 5.3c1.36 0 2.72.2 4.09.75v3.88a9.23 9.23 0 0 0-4.1-1.06c-.86 0-1.44.25-1.44.9 0 1.85 6.29.97 6.29 5.88z"
                fill="white"
              />
            </svg>
            <div className="h-6 w-px bg-white/20"></div>
            <span className="text-white/60 text-xs">
              Secure Payment Processing
            </span>
          </div>
          {/* </div> */}
        </div>

        <div className="text-center">
          <p className="text-white/80 text-md sm:text-sm lg:text-base mb-4 sm:mb-6 px-2">
            {footer}
          </p>
        </div>
      </div>

      {/* Modal */}
      <Modal
        title=""
        className="modal-update create-modal"
        centered
        visible={confirmPaymentModal}
        closable={true}
        onCancel={() => {
          setConfirmPaymentModal(false);
          dispatch(resetStateForPromoCode());
          setPromoCode("");
        }}
        footer={false}
        width={600}
      >
        <form className="add-user-popup p-6 rounded-xl" onSubmit={handleSubmit}>
          {/* Header */}
          <div className="modal-add-user create-post-modal text-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900">
              Apply Your Code
            </h3>
          </div>

          {/* Promo Code Input */}
          <div className="max-w-md mx-auto mb-6">
            <label className="block mb-2 text-gray-900 font-semibold">
              Promo Code (optional)
            </label>
            <input
              type="text"
              placeholder="Enter code"
              className="w-full px-4 py-3 rounded-lg border border-gray-400 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#f16480] focus:border-[#f16480] transition-all duration-300"
              value={promoCode}
              onChange={(e) => {
                setPromoCode(e.target.value);
                debounce(
                  () =>
                    dispatch(
                      verifyPromoCodeLoad({
                        promocode: e.target.value,
                      })
                    ),
                  500
                );
              }}
            />
          </div>

          {/* Price Update */}
          <div className="text-center mb-6">
            {promoCodeData?.isVerify && !verifypromoCodeLoading ? (
              <>
                <p className="text-gray-700 text-2xl font-medium">
                  New Price:{" "}
                  <span className="text-[#f16480] text-3xl font-bold">
                    ${amount}
                  </span>
                </p>
                <p className="text-gray-400 text-xl  mt-1">
                  Originally ${plans.find((p) => p.id === selectedPlan)?.price}
                </p>
              </>
            ) : verifypromoCodeLoading ? (
              <Spin spinning={true} />
            ) : null}
          </div>

          {/* Continue Button */}
          <div className="text-center">
            <button
              type="submit"
              className="bg-gradient-to-r from-[#6162ac] to-[#f16480] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-bold text-base shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
            >
              Continue
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default SubscriptionScreen;
